import React, { useEffect, useCallback, useReducer, memo } from "react";
import MenuBottomBar from "./MenuBottomBar";
import {
  Menu,
  Search,
  Headphones,
  Bell,
  Upload,
  User,
  X,
  // Film removed
  Home,
  Plus,
  FileText,
  Info,
  ScrollText,
  Bookmark,
  MessageSquare,
  Settings,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import MessagesModal from "./Messages/MessagesModal";
import NotificationsModal from "./NotificationsModal";
import UserMenu from "./left menu/UserMenu";
import ThemeToggle from "./left menu/ThemeToggle";
import { useAuth } from "../lib/auth";
import { SearchBar } from '@/components/SearchBar';

// Memoize modal components to prevent unnecessary re-renders
const MemoizedMessagesModal = memo(MessagesModal);
const MemoizedNotificationsModal = memo(NotificationsModal);
const MemoizedUserMenu = memo(UserMenu);

interface NavbarProps {
  collapsed?: boolean;
  onUpload?: () => void;
  _onToggleCollapse?: () => void;
  children?: React.ReactNode;
}

/**
 * Renders a responsive navigation bar with sidebar, mobile menu, bottom navigation, and modal management.
 *
 * The Navbar adapts its layout for mobile and desktop screens, providing navigation links, user profile access, theme toggling, and modal dialogs for messages, notifications, friend requests, and user menu. It manages UI state for menus and modals, handles navigation, and supports sidebar collapse via an optional callback.
 *
 * @param collapsed - If true, renders the sidebar in a collapsed state.
 * @param onUpload - Callback invoked when the upload button is clicked.
 * @param onToggleCollapse - Optional callback to toggle the sidebar's collapsed state.
 * @param children - Child elements to be rendered inside the navbar.
 */
export default function Navbar({
  collapsed = false,
  onUpload,
  // onToggleCollapse param removed as it was unused
  children,
}: NavbarProps) {
  // Use location to determine active navigation items
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Listen for auth state changes and update UI accordingly
  useEffect(() => {
    if (!user) {
      // If user is logged out, close all modals
      dispatch({ type: "CLOSE_ALL_MODALS" });
    }
  }, [user]);

  // Helper function to check if a path is active
  const isPathActive = useCallback(
    (path: string) => {
      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    },
    [location]
  );

  // Define UI state reducer for more efficient state updates
  type UiStateAction =
    | { type: "SET_STATE"; key: string; value: boolean }
    | { type: "RESET_ALL" }
    | { type: "CLOSE_ALL_MODALS" };

  const initialUiState = {
    isMenuOpen: false,
    isSearchOpen: false,
    isMessagesOpen: false,
    isNotificationsOpen: false,
    isUserMenuOpen: false,
  };

  const uiStateReducer = (
    state: typeof initialUiState,
    action: UiStateAction
  ) => {
    switch (action.type) {
      case "SET_STATE":
        return { ...state, [action.key]: action.value };
      case "RESET_ALL":
        return initialUiState;
      case "CLOSE_ALL_MODALS":
        return {
          ...state,
          isMessagesOpen: false,
          isNotificationsOpen: false,
          isUserMenuOpen: false,
          isFriendRequestsOpen: false,
        };
      default:
        return state;
    }
  };

  const [uiState, dispatch] = useReducer(uiStateReducer, initialUiState);

  // Memoized helper functions to update UI state properties
  const updateUiState = useCallback(
    (key: keyof typeof uiState, value: boolean) => {
      // Close other modals when opening a new one to prevent multiple modals
      if (
        value === true &&
        (key === "isMessagesOpen" ||
          key === "isNotificationsOpen" ||
          key === "isUserMenuOpen")
      ) {
        dispatch({ type: "CLOSE_ALL_MODALS" });
      }
      dispatch({ type: "SET_STATE", key, value });
    },
    []
  );

  // Reset all UI states
  const resetAllUiStates = useCallback(() => {
    dispatch({ type: "RESET_ALL" });
  }, []);

  // Close all modals
  const closeAllModals = useCallback(() => {
    dispatch({ type: "CLOSE_ALL_MODALS" });
  }, []);

  // Handle navigation and close modals
  const handleNavigation = useCallback(
    (path: string) => {
      console.log("Navigating to:", path);
      // First navigate, then close modals
      navigate(path);
      // Use setTimeout to ensure navigation happens before closing modals
      setTimeout(() => {
        updateUiState("isMenuOpen", false);
        closeAllModals();
      }, 0);
    },
    [navigate, updateUiState, closeAllModals]
  );

  const closeMessagesModal = useCallback(
    () => updateUiState("isMessagesOpen", false),
    [updateUiState]
  );
  const closeNotificationsModal = useCallback(
    () => updateUiState("isNotificationsOpen", false),
    [updateUiState]
  );
  const closeUserMenu = useCallback(
    () => updateUiState("isUserMenuOpen", false),
    [updateUiState]
  );

  const menuItems = [
    { icon: Search, label: "Search", path: "/search" },
    // Reels item has been removed
    { icon: Headphones, label: "Music", path: "/music" },
    { icon: FileText, label: "Pages", path: "/pages" },
    { icon: Bookmark, label: "Bookmarks", path: "/bookmarks" },
    // Hidden for launch
    // { icon: Radio, label: "Live", path: "/live" },
    // { icon: Calendar, label: "Events", path: "/events" },
  ];

  // Use the same menu items for both mobile and desktop

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Close any open modals or menus when Escape is pressed
        if (Object.values(uiState).some((value) => value === true)) {
          resetAllUiStates();
        }
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [resetAllUiStates]);

  // Handle clicks outside modals to close them
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      // Only handle if any modal is open
      if (
        uiState.isMessagesOpen ||
        uiState.isNotificationsOpen ||
        uiState.isUserMenuOpen
      ) {
        // Check if click is outside modal content
        const target = e.target as HTMLElement;
        if (target && !target.closest(".modal-content")) {
          // closeAllModals();
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [uiState, closeAllModals]);

  return (
    <>
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-black border-b border-gray-200 dark:border-transparent flex items-center justify-between px-4 md:hidden z-50 header-container">
        <div className="flex items-center">
          <button
            onClick={() => updateUiState("isMenuOpen", true)}
            className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link
            to="/"
            className="text-xl font-bold text-primary-800 dark:text-primary-400 truncate ml-2"
          >
            <img src="/images/logo-m.svg" alt="MoZo" className="h-8 w-auto" />
          </Link>
        </div>

        <div className="flex items-center space-x-2">
          <SearchBar />
        </div>
      </div>

      {/* Mobile Menu Sidebar */}
      {uiState.isMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => updateUiState("isMenuOpen", false)}
          />
          <div className="absolute top-0 left-0 w-[80%] max-w-[250px] h-full bg-white dark:bg-gray-900 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-primary-800 dark:text-primary-400">
                Menu
              </h2>
              <button
                onClick={() => updateUiState("isMenuOpen", false)}
                className="p-2 text-gray-600 dark:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-1">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleNavigation(item.path)}
                  className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              ))}

              {/* Additional Links for mobile */}
              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <button
                  onClick={() => handleNavigation("/company/about")}
                  className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  <Info className="w-5 h-5" />
                  <span>About</span>
                </button>
                <button
                  onClick={() => handleNavigation("/creators/terms")}
                  className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  <ScrollText className="w-5 h-5" />
                  <span>Terms</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 py-2 px-4 z-50">
        <div className="flex justify-between items-center">
          <Link
            to="/feed"
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white"
          >
            <Home className="w-6 h-6" />
          </Link>
          <Link
            to="/music"
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white"
          >
            <Headphones className="w-6 h-6" />
          </Link>
          <button
            onClick={onUpload}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white"
          >
            <Plus className="w-6 h-6" />
          </button>
          <button
            onClick={() => updateUiState("isMessagesOpen", true)}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white"
          >
            <MessageSquare className="w-6 h-6" />
          </button>
          <button
            onClick={() => updateUiState("isNotificationsOpen", true)}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white"
          >
            <Bell className="w-6 h-6" />
          </button>
          <button
            onClick={() => updateUiState("isUserMenuOpen", true)}
            className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center -mt-4 sm:-mt-6 bg-white dark:bg-black rounded-xl shadow-lg border border-gray-200 dark:border-transparent"
          >
            {user?.user_metadata?.avatar_url ? (
              <img
                src={user.user_metadata.avatar_url}
                alt="Profile"
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover"
              />
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Desktop Sidebar Navigation */}
      <nav
        className={`fixed left-0 top-0 h-screen bg-white dark:bg-black border-r border-transparent z-50 flex-col transition-all duration-300 hidden md:flex ${collapsed ? "w-16" : "w-64"}`}
      >
        <div className="p-4">
          <button
            onClick={() => navigate("/")}
            className="w-full text-left hover:opacity-80 transition-opacity"
          >
            {collapsed ? (
              <img src="/images/logo-m.svg" alt="MoZo" className="h-8 w-auto" />
            ) : (
              <>
                <img
                  src="/images/logo-d.svg"
                  alt="MoZo.Live"
                  className="h-8 w-auto"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400 block">
                  Making Moments Matter
                </span>
              </>
            )}
          </button>
        </div>

        <div className="px-2 mb-8">
          <button
            onClick={() => updateUiState("isUserMenuOpen", true)}
            className="flex items-center w-full px-4 py-4 rounded-lg transition-colors text-gray-600 dark:text-gray-300 hover:text-white dark:hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900]"
          >
            <div className="relative flex items-center justify-center w-8 h-8">
              {user?.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt="Profile"
                  className="w-8 h-8 rounded-lg object-cover"
                />
              ) : (
                <User className="w-8 h-8" />
              )}
            </div>
            {!collapsed && (
              <div className="ml-3 flex flex-col items-start">
                <span className="font-medium text-sm truncate max-w-[120px]">
                  {user?.user_metadata?.full_name || "User"}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                  @{user?.user_metadata?.username || "username"}
                </span>
              </div>
            )}
          </button>
        </div>

        {/* Scrollable Navigation Area */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-2 pb-4">
          {/* Main Navigation Items */}
          <div className="space-y-3">
            {menuItems.map((item, index) => (
              <NavItem
                key={index}
                icon={<item.icon />}
                label={item.label}
                active={isPathActive(item.path)}
                collapsed={collapsed}
                onClick={() => handleNavigation(item.path)}
              />
            ))}
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto mt-4">
            {children || null}
          </div>
        </div>

        {/* Bottom Menu Bar with action buttons above it */}
        <div className="absolute bottom-0 left-0 right-0 flex flex-col">
          {/* Settings and Theme Toggle - Each on their own line */}
          <div className="flex flex-col space-y-2 px-2 py-3 mb-2" data-component-name="Navbar">
            <button
              onClick={() => navigate("/settings")}
              className={`flex items-center w-full px-4 py-2 rounded-lg transition-colors text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white ${collapsed ? "justify-center" : ""}`}
            >
              <div className="relative flex items-center justify-center w-8 h-8">
                <Settings className="w-8 h-8" />
              </div>
              {!collapsed && <span className="ml-3">Settings</span>}
            </button>
            <div 
              className={`flex items-center w-full px-4 py-2 rounded-lg transition-colors text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white ${collapsed ? "justify-center" : ""} cursor-pointer`}
              data-component-name="Navbar"
            >
              <ThemeToggle showLabel={!collapsed} />
            </div>
          </div>
          
          {/* Action buttons - Reordered as requested */}
          <div className="flex space-x-2 p-2 mb-2" data-component-name="Navbar">
            <button
              onClick={() => updateUiState("isMessagesOpen", true)}
              className="flex-1 flex items-center justify-center p-2 rounded-lg hover:bg-[#065900] dark:hover:bg-[#065900] text-gray-700 dark:text-gray-300 hover:text-white dark:hover:text-white"
            >
              <div className="relative flex items-center justify-center w-8 h-8">
                <MessageSquare className="w-8 h-8" />
              </div>
            </button>
            <button
              onClick={() => updateUiState("isNotificationsOpen", true)}
              className="flex-1 flex items-center justify-center p-2 rounded-lg hover:bg-[#065900] dark:hover:bg-[#065900] text-gray-700 dark:text-gray-300 hover:text-white dark:hover:text-white"
            >
              <div className="relative flex items-center justify-center w-8 h-8">
                <Bell className="w-8 h-8" />
              </div>
            </button>
            <button
              onClick={onUpload}
              className="flex-1 flex items-center justify-center p-2 rounded-lg hover:bg-[#065900] dark:hover:bg-[#065900] text-gray-700 dark:text-gray-300 hover:text-white dark:hover:text-white"
            >
              <div className="relative flex items-center justify-center w-8 h-8">
                <Upload className="w-8 h-8" />
              </div>
            </button>
          </div>
          
          {/* Bottom Menu Bar */}
          <MenuBottomBar />
        </div>
      </nav>

      {/* Modals - Only render when needed */}
      {uiState.isMessagesOpen && (
        <MemoizedMessagesModal
          isOpen={uiState.isMessagesOpen}
          onClose={closeMessagesModal}
        />
      )}
      {uiState.isNotificationsOpen && (
        <MemoizedNotificationsModal
          isOpen={uiState.isNotificationsOpen}
          onClose={closeNotificationsModal}
        />
      )}
      {uiState.isUserMenuOpen && (
        <MemoizedUserMenu
          isOpen={uiState.isUserMenuOpen}
          onClose={closeUserMenu}
        />
      )}
    </>
  );
}

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  collapsed?: boolean;
  onClick?: () => void;
  badge?: string;
}

// Memoize the NavItem component to prevent unnecessary re-renders
const NavItem = memo(function NavItem({
  icon,
  label,
  active = false,
  collapsed = false,
  onClick,
  badge,
}: NavItemProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center w-full px-4 py-2 rounded-lg transition-colors ${active
          ? "text-white bg-[#065900] dark:text-white dark:bg-[#065900]"
          : "text-gray-600 dark:text-gray-300 hover:text-white hover:bg-[#065900] dark:hover:bg-[#065900] dark:hover:text-white"
        } ${collapsed ? "justify-center" : ""}`}
    >
      <div className="relative flex items-center justify-center w-8 h-8">
        {React.cloneElement(icon as React.ReactElement, {
          className: "w-8 h-8"
        })}
        {badge && !collapsed && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
            {badge}
          </span>
        )}
      </div>
      {!collapsed && <span className="ml-3">{label}</span>}
      {collapsed && badge && (
        <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );
});
